import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useEffect,
  useRef,
  ReactNode,
} from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { useToast } from "../../../foundation/altzero_base/base/hooks/use-toast";
import {
  GeneratedDocumentData,
  DocumentTheme,
  Section,
  AIPromptData,
  Block,
} from "../types/documentTypes";
import { themePresets } from "../utils/themeUtils";
import {
  convertContentToBlocks,
  convertBlocksToContent,
  splitIntoPages,
} from "../utils/documentUtils";

// 🚨 EMERGENCY FIX: Create pages directly without using problematic splitIntoPages
const createPagesDirectly = (sections: any[]) => {
  if (!sections || sections.length === 0) return [];

  const sectionsPerPage = 3;
  const pages = [];

  for (let i = 0; i < sections.length; i += sectionsPerPage) {
    const pageNumber = Math.floor(i / sectionsPerPage) + 1;
    const sectionsForPage = sections.slice(i, i + sectionsPerPage);

    pages.push({
      id: `page-${pageNumber}`,
      content: sectionsForPage
    });
  }

  console.log(`🚨 EMERGENCY: Created ${pages.length} pages with all ${sections.length} sections preserved`);
  return pages;
};
import {
  fetchDocument,
  saveDocument,
  regenerateSectionWithAI,
  convertEditorJsonToBlocks,
} from "../services/documentService";
import { supabase } from "../../../foundation/altzero_base/base/utils/supabaseClient";

interface DocumentContextProps {
  documentData: GeneratedDocumentData;
  editableSections: Section[];
  documentTheme: DocumentTheme;
  isLoading: boolean;
  isSaving: boolean;
  isEdited: boolean;
  overview: string;
  documentDbId: string | null;
  clientId: string | null;
  promptTemplateId: string | null;
  scopeTemplateId: string | null;
  filePath: string | null;
  aiPromptsSummary: AIPromptData;
  selectedSectionId: string | null;
  regeneratingSection: string | null;
  pages: any[];
  currentPage: number;

  // Methods
  setDocumentData: React.Dispatch<React.SetStateAction<GeneratedDocumentData>>;
  setEditableSections: React.Dispatch<React.SetStateAction<Section[]>>;
  setDocumentTheme: React.Dispatch<React.SetStateAction<DocumentTheme>>;
  handleUpdateSectionTitle: (sectionId: string, newTitle: string) => void;
  handleUpdateBlock: (
    sectionId: string,
    blockId: string,
    updates: Partial<Block>
  ) => void;
  handleUpdateSectionContent: (sectionId: string, newContent: string) => void;
  handleAddBlock: (sectionId: string, type: Block["type"]) => void;
  handleDeleteBlock: (sectionId: string, blockId: string) => void;
  handleDeleteSection: (sectionId: string) => void;
  handleRegenerateWithAI: (
    sectionId: string,
    enhancementType: string
  ) => Promise<void>;
  setCurrentPage: (page: number) => void;
  setSelectedSectionId: (id: string | null) => void;
  saveDocumentToSupabase: () => Promise<any>;
  applyThemePreset: (
    presetName: "professional" | "modern" | "creative" | "formal"
  ) => void;
  saveDocumentTheme: () => void;
  updateThemeProperty: (property: keyof DocumentTheme, value: string) => void;
  updateHeaderProperty: (property: string, value: any) => void;
  updateFooterProperty: (property: string, value: any) => void;
  handleLogoUpload: () => void;
  saveDocumentStateToSession: () => void;
}

export const DocumentContext = createContext<DocumentContextProps | null>(null);

export const useDocumentContext = () => {
  const context = useContext(DocumentContext);
  if (!context) {
    throw new Error(
      "useDocumentContext must be used within a DocumentProvider"
    );
  }
  return context;
};

interface DocumentProviderProps {
  children: ReactNode;
}

export const DocumentProvider: React.FC<DocumentProviderProps> = ({
  children,
}) => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { toast } = useToast();

  const documentId = searchParams?.get("id");
  const titleFromURL = searchParams?.get("title");
  const preserveOriginal = searchParams?.get("preserveOriginal") === "true";

  // State variables
  const [documentData, setDocumentData] = useState<GeneratedDocumentData>({
    title: "",
    client: "",
    type: "",
    sections: [],
  });
  const [editableSections, setEditableSections] = useState<Section[]>([]);
  const [documentTheme, setDocumentTheme] = useState<DocumentTheme>(
    themePresets.professional
  );
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [isEdited, setIsEdited] = useState(false);
  const [overview, setOverview] = useState("");
  const [documentDbId, setDocumentDbId] = useState<string | null>(null);
  const [clientId, setClientId] = useState<string | null>(null);
  const [promptTemplateId, setPromptTemplateId] = useState<string | null>(null);
  const [scopeTemplateId, setScopeTemplateId] = useState<string | null>(null);
  const [filePath, setFilePath] = useState<string | null>(null);
  const [aiPromptsSummary, setAiPromptsSummary] = useState<AIPromptData>({
    styleGuidance: "",
    contentFocus: "",
    additionalInstructions: "",
  });
  const [selectedSectionId, setSelectedSectionId] = useState<string | null>(
    null
  );
  const [regeneratingSection, setRegeneratingSection] = useState<string | null>(
    null
  );
  const [pages, setPages] = useState<any[]>([]);
  const [currentPage, setCurrentPage] = useState(0);

  // 🚨 FIX: Helper function to save document state to sessionStorage with correct key
  const saveDocumentStateToSession = useCallback(() => {
    // Use appropriate key based on document state
    let sessionKey;
    if (documentId) {
      sessionKey = `document_${documentId}`;
    } else if (documentDbId) {
      // For saved documents, use the generated document key
      sessionKey = "scopingai_generated_document";
    } else {
      // For new unsaved documents, use proposal data key
      sessionKey = "scopingai_proposal_data";
    }
    const sessionData = {
      documentData,
      editableSections,
      documentTheme,
      documentDbId,
      clientId,
      promptTemplateId,
      scopeTemplateId,
      filePath,
      aiPromptsSummary,
      overview,
      isExistingDocument: !!documentId,
      isAutoSaved: false, // Will be set to true after auto-save
      timestamp: Date.now(),
    };

    try {
      sessionStorage.setItem(sessionKey, JSON.stringify(sessionData));
      console.log("💾 Saved document state to sessionStorage:", sessionKey);
    } catch (error) {
      console.error("❌ Failed to save document state to sessionStorage:", error);
    }
  }, [documentData, editableSections, documentTheme, documentDbId, clientId, promptTemplateId, scopeTemplateId, filePath, aiPromptsSummary, overview, documentId]);

  // Show a toast notification when content is preserved from original
  useEffect(() => {
    if (preserveOriginal && !isLoading) {
      toast({
        title: "Content preserved from reference document",
        description:
          "This document contains the original content from your reference document. You can edit it or use AI to enhance specific sections.",
        variant: "default",
      });
    }
  }, [preserveOriginal, isLoading, toast]);

  // Update pages when editableSections change (for live preview)
  useEffect(() => {
    if (editableSections.length > 0) {
      console.log('🚨 EMERGENCY FIX: Creating pages directly from all sections:', {
        sectionsCount: editableSections.length,
        sectionIds: editableSections.map(s => s.id),
        sectionTitles: editableSections.map(s => s.title)
      });

      // 🚨 EMERGENCY FIX: Create pages directly without using splitIntoPages
      // Put 3 sections per page to ensure all sections are included
      const sectionsPerPage = 3;
      const newPages = [];

      for (let i = 0; i < editableSections.length; i += sectionsPerPage) {
        const pageNumber = Math.floor(i / sectionsPerPage) + 1;
        const sectionsForPage = editableSections.slice(i, i + sectionsPerPage);

        newPages.push({
          id: `page-${pageNumber}`,
          content: sectionsForPage
        });

        console.log(`📄 EMERGENCY: Created page ${pageNumber} with sections:`, sectionsForPage.map(s => s.title));
      }

      setPages(newPages);

      console.log('🚨 EMERGENCY FIX COMPLETE:', {
        pagesCreated: newPages.length,
        totalSectionsInPages: newPages.reduce((total, page) => total + (page.content?.length || 0), 0),
        allSectionsPreserved: newPages.reduce((total, page) => total + (page.content?.length || 0), 0) === editableSections.length
      });
    }
  }, [editableSections]);

  // Load document data when component mounts
  useEffect(() => {
    const loadDocumentData = async () => {
      try {
        setIsLoading(true);

        // Always reset currentPage to 0 when loading new data
        setCurrentPage(0);

        if (documentId) {
          // 🚨 ENHANCED: Improved loading logic for existing documents with auto-save support
          console.log("🔍 Loading existing document with ID:", documentId);

          // 🚨 NEW: Check if this document was auto-saved and get the real documentDbId
          let actualDocumentId = documentId;
          let documentDbIdFromSession = null;

          try {
            // Check for auto-saved document data
            const generatedDocData = sessionStorage.getItem("scopingai_generated_document");
            if (generatedDocData) {
              const parsed = JSON.parse(generatedDocData);
              if (parsed.documentDbId) {
                documentDbIdFromSession = parsed.documentDbId;
                console.log("🔍 Found auto-saved documentDbId in session:", documentDbIdFromSession);
              }
            }

            // Also check document-specific session data
            const sessionData = sessionStorage.getItem(`document_${documentId}`);
            if (sessionData) {
              const parsedData = JSON.parse(sessionData);
              if (parsedData.documentDbId) {
                documentDbIdFromSession = parsedData.documentDbId;
                console.log("🔍 Found documentDbId in document session:", documentDbIdFromSession);
              }

              const { data: { user } } = await supabase.auth.getUser();
              // If clientId matches user ID, it's corrupted - remove it
              if (parsedData.clientId === user?.id) {
                console.warn("Removing corrupted session data with invalid clientId");
                sessionStorage.removeItem(`document_${documentId}`);
              }
            }
          } catch (error) {
            console.error("Error checking session data:", error);
          }
          // 🚨 PHASE 4: Improved loading priority logic
          let documentDataFromSession = null;
          try {
            // First check if this is an existing document from database
            const existingDocSessionData = sessionStorage.getItem(`document_${documentId}`);
            if (existingDocSessionData) {
              const parsedExistingData = JSON.parse(existingDocSessionData);
              // If session data indicates this is an existing document, load from database
              if (parsedExistingData.isExistingDocument) {
                console.log("🔍 Existing document found in session, will load from database");
              } else {
                documentDataFromSession = parsedExistingData;
                console.log("🔍 Using existing document data from sessionStorage");
              }
            }

            // If no existing document session data, check for proposal data (new documents)
            if (!documentDataFromSession) {
              const proposalData = sessionStorage.getItem("scopingai_proposal_data");

              // If documentId starts with "proposal_", try the general proposal data key
              if (documentId?.startsWith("proposal_")) {
                console.log(
                  "🔍 Document ID is a proposal, checking scopingai_proposal_data..."
                );
                console.log(
                  "🔍 All session storage keys:",
                  Object.keys(sessionStorage)
                );
                console.log("🔍 Proposal data found:", !!proposalData);
                if (proposalData) {
                  console.log("🔍 Proposal data length:", proposalData.length);
                }
                if (proposalData) {
                  const parsedProposalData = JSON.parse(proposalData);
                  console.log("📊 Raw proposal data:", parsedProposalData);

                  if (parsedProposalData.proposal) {
                    console.log(
                      "📄 Proposal sections:",
                      parsedProposalData.proposal.sections
                    );

                    // 🚨 FIXED: Transform proposal data without duplicating sections
                    const processedSections = parsedProposalData.proposal.sections?.map(
                      (section: any, index: number) => ({
                        id: `section-${index + 1}-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
                        title: section.title || "Untitled Section",
                        content: section.content || "",
                        blocks: [
                          { type: "text", content: section.content || "" },
                        ],
                      })
                    ) || [];

                    const transformedData = {
                      documentData: {
                        title:
                          parsedProposalData.proposal.clientName ||
                          "Generated Proposal",
                        client: parsedProposalData.proposal.clientName || "",
                        type: "client_proposal",
                        sections: processedSections, // 🚨 FIXED: Use same sections reference
                      },
                      sections: processedSections, // 🚨 FIXED: Use same sections reference to prevent duplication
                      theme: parsedProposalData.theme || {},
                    };

                    documentDataFromSession = transformedData;
                    console.log("✅ Transformed proposal data:", transformedData);
                    console.log(
                      "📊 Sections count:",
                      transformedData.sections.length
                    );
                  }
                }
              }
            }
          } catch (error) {
            console.error("Error reading from session storage:", error);
          }

          if (documentDataFromSession) {
            // 🚨 PHASE 6: Enhanced refresh handling with proper state preservation
            console.log(
              "📄 Loading document from session storage:",
              documentDataFromSession
            );
            console.log(
              "📊 Document sections count:",
              documentDataFromSession.sections?.length || 0
            );

            // Check if this is a refresh scenario (document has documentDbId)
            const isRefreshScenario = !!documentDataFromSession.documentDbId;
            console.log("🔄 Refresh scenario detected:", isRefreshScenario);

            if (isRefreshScenario) {
              // For refresh scenarios, prioritize database loading but use session for state
              console.log("🔄 Refresh: Will load from database but preserve session state");

              // Set documentDbId from session to ensure button shows "Update"
              if (documentDataFromSession.documentDbId) {
                setDocumentDbId(documentDataFromSession.documentDbId);
                console.log("✅ Restored documentDbId from session:", documentDataFromSession.documentDbId);
              }

              // Set other metadata from session
              if (documentDataFromSession.clientId) {
                setClientId(documentDataFromSession.clientId);
              }
              if (documentDataFromSession.promptTemplateId) {
                setPromptTemplateId(documentDataFromSession.promptTemplateId);
              }
              if (documentDataFromSession.scopeTemplateId) {
                setScopeTemplateId(documentDataFromSession.scopeTemplateId);
              }
              if (documentDataFromSession.filePath) {
                setFilePath(documentDataFromSession.filePath);
              }
              if (documentDataFromSession.aiPromptsSummary) {
                setAiPromptsSummary(documentDataFromSession.aiPromptsSummary);
              }
              if (documentDataFromSession.overview) {
                setOverview(documentDataFromSession.overview);
              }

              // Continue to database loading logic below
            } else {
              // Not a refresh scenario - use session data directly
              console.log("📄 Using session data directly (not a refresh scenario)");
            }

            // 🚨 FIXED: Ensure sections are only loaded from one source to prevent duplication
            const rawSectionsFromSession = documentDataFromSession.sections ||
                                          documentDataFromSession.documentData?.sections ||
                                          [];

            // 🚨 FIXED: NO DEDUPLICATION - Keep all sections as-is
            console.log('🔍 LOADING ALL SECTIONS WITHOUT DEDUPLICATION:', {
              sectionsCount: rawSectionsFromSession.length,
              sectionIds: rawSectionsFromSession.map((s: any) => s.id),
              sectionTitles: rawSectionsFromSession.map((s: any) => s.title)
            });

            // Use all sections without any filtering
            const sectionsFromSession = rawSectionsFromSession;

            console.log('🔍 Loading sections from session:', {
              fromSections: documentDataFromSession.sections?.length || 0,
              fromDocumentData: documentDataFromSession.documentData?.sections?.length || 0,
              rawCount: rawSectionsFromSession.length,
              deduplicatedCount: sectionsFromSession.length,
              duplicatesRemoved: rawSectionsFromSession.length - sectionsFromSession.length
            });

            setDocumentData({
              ...(documentDataFromSession.documentData || {}),
              title: documentDataFromSession.documentData?.title || titleFromURL || "",
              client: documentDataFromSession.documentData?.client || "",
              type: documentDataFromSession.documentData?.type || "",
              sections: sectionsFromSession, // Use the same sections reference
            });

            setEditableSections(sectionsFromSession);

            // 🚨 FIXED: Initialize pages from the unified sections source
            if (sectionsFromSession && sectionsFromSession.length > 0) {
              console.log(
                "Loading sections from session:",
                sectionsFromSession
              );
              const newPages = createPagesDirectly(sectionsFromSession);
              setPages(newPages);
              console.log("Created pages from session:", newPages);
            }

            // Set theme and merge with logo/branding data from session storage
            let finalTheme = documentDataFromSession.theme || {};

            // Check for logo and branding data in session storage
            try {
              const brandingData = sessionStorage.getItem(
                `document_${documentId}_branding`
              );
              if (brandingData) {
                const parsedBranding = JSON.parse(brandingData);
                console.log(
                  "📦 Found logo/branding data in session storage (from session):",
                  parsedBranding
                );

                // Merge logo data into theme
                if (parsedBranding.logo) {
                  finalTheme.logo = {
                    src: parsedBranding.logo.src,
                    position: parsedBranding.logo.position || "header-left",
                    size: parsedBranding.logo.size || "medium",
                    opacity: parsedBranding.logo.opacity || 100,
                    showOnAllPages:
                      parsedBranding.logo.showOnAllPages !== false,
                  };
                  console.log(
                    "✅ Merged logo data into session theme:",
                    finalTheme.logo
                  );
                }

                // Merge branding data into theme
                if (parsedBranding.branding) {
                  finalTheme.brandTitle = parsedBranding.branding.brandTitle;
                  finalTheme.tagline = parsedBranding.branding.tagline;
                  finalTheme.primaryColor =
                    parsedBranding.branding.primaryColor;
                  finalTheme.secondaryColor =
                    parsedBranding.branding.secondaryColor;
                  console.log("✅ Merged branding data into session theme");
                }

                // Clean up session storage after loading
                sessionStorage.removeItem(`document_${documentId}_branding`);
              }
            } catch (error) {
              console.warn(
                "⚠️ Error loading branding data from session storage (session path):",
                error
              );
            }

            if (Object.keys(finalTheme).length > 0) {
              setDocumentTheme(finalTheme);
            }

            // Set additional metadata from session
            if (documentDataFromSession.overview) {
              setOverview(documentDataFromSession.overview);
            }

            if (documentDataFromSession.documentDbId) {
              setDocumentDbId(documentDataFromSession.documentDbId);
            }

            if (documentDataFromSession.clientId) {
              // Validate that clientId is not the same as user ID
              const {
                data: { user },
              } = await supabase.auth.getUser();
              if (documentDataFromSession.clientId !== user?.id) {
                setClientId(documentDataFromSession.clientId);
              } else {
                console.warn(
                  "Invalid clientId detected (matches user ID), setting to null"
                );
                setClientId(null);
              }
            }

            if (documentDataFromSession.promptTemplateId) {
              setPromptTemplateId(documentDataFromSession.promptTemplateId);
            }

            if (documentDataFromSession.scopeTemplateId) {
              setScopeTemplateId(documentDataFromSession.scopeTemplateId);
            }

            if (documentDataFromSession.filePath) {
              setFilePath(documentDataFromSession.filePath);
            }

            if (documentDataFromSession.aiPromptsSummary) {
              setAiPromptsSummary(documentDataFromSession.aiPromptsSummary);
            }
          } else {
            // Check if this is a proposal document that should not be loaded from database
            if (documentId?.startsWith("proposal_")) {
              console.log(
                "⚠️ Proposal document not found in session storage, creating fallback"
              );

              // Create fallback content for proposal documents
              const fallbackSections = [
                {
                  id: "section-1",
                  title: "Document Loading",
                  content:
                    "This document is being loaded. If this persists, please try generating the document again.",
                  blocks: convertContentToBlocks(
                    "This document is being loaded. If this persists, please try generating the document again."
                  ),
                  status: "completed" as const,
                  order: 1,
                },
              ];

              setEditableSections(fallbackSections);
              const fallbackPages = createPagesDirectly(fallbackSections);
              setPages(fallbackPages);

              setDocumentData({
                title: titleFromURL || "Generated Proposal",
                client: "",
                type: "client_proposal",
                sections: fallbackSections,
              });

              console.log("📄 Set fallback content for proposal document");
              return; // Don't try to load from database
            }

            // 🚨 ENHANCED: Fetch from database using documentDbId if available
            const idToUse = documentDbIdFromSession || documentId;
            console.log("Loading document from database:", {
              originalId: documentId,
              documentDbIdFromSession,
              idToUse,
              idType: typeof idToUse
            });

            try {
              const result = await fetchDocument(idToUse);
              console.log("fetchDocument result:", result);

              if (result?.document) {
                console.log("Fetched document from database:", result.document);

                // Set document data
                setDocumentData(result.document);
                setDocumentDbId(result.document.id?.toString() || documentId);

                // Create editable sections from document sections (using Markdown)
                console.log('🔍 DATABASE SECTIONS DEBUG:', {
                  rawSectionsCount: result.document.sections?.length || 0,
                  rawSectionIds: result.document.sections?.map((s: any) => s.id) || [],
                  rawSectionTitles: result.document.sections?.map((s: any) => s.title) || []
                });

                const sectionsWithBlocks = result.document.sections.map(
                  (section: any) => {
                    // Ensure content is a string (Markdown format)
                    const content =
                      typeof section.content === "string"
                        ? section.content
                        : JSON.stringify(section.content) || "";

                    console.log(`🔍 Processing DB section: "${section.title}"`, {
                      id: section.id,
                      contentLength: content?.length || 0,
                      hasBlocks: !!section.blocks,
                      contentPreview: content?.substring(0, 100) || 'NO CONTENT'
                    });

                    return {
                      ...section,
                      content, // Ensure content is always a string
                      blocks: section.blocks || convertContentToBlocks(content),
                    };
                  }
                );

                console.log(
                  "🔍 Loading sections from database:",
                  result.document.sections
                );
                console.log("🔍 Sections with blocks:", sectionsWithBlocks);

                console.log('🔍 SETTING EDITABLE SECTIONS FROM DATABASE:', {
                  sectionsCount: sectionsWithBlocks.length,
                  sectionIds: sectionsWithBlocks.map(s => s.id),
                  sectionTitles: sectionsWithBlocks.map(s => s.title)
                });

                setEditableSections(sectionsWithBlocks);

                // Initialize pages after setting sections
                const newPages = createPagesDirectly(sectionsWithBlocks);
                setPages(newPages);
                console.log("🔍 Created pages from database:", newPages);

                // Set theme if available and merge with logo/branding data from session storage
                let finalTheme = result.document.theme || {};

                // Check for logo and branding data in session storage
                try {
                  const brandingData = sessionStorage.getItem(
                    `document_${documentId}_branding`
                  );
                  if (brandingData) {
                    const parsedBranding = JSON.parse(brandingData);
                    console.log(
                      "📦 Found logo/branding data in session storage:",
                      parsedBranding
                    );

                    // Merge logo data into theme
                    if (parsedBranding.logo) {
                      finalTheme.logo = {
                        src: parsedBranding.logo.src,
                        position: parsedBranding.logo.position || "header-left",
                        size: parsedBranding.logo.size || "medium",
                        opacity: parsedBranding.logo.opacity || 100,
                        showOnAllPages:
                          parsedBranding.logo.showOnAllPages !== false,
                      };
                      console.log(
                        "✅ Merged logo data into document theme:",
                        finalTheme.logo
                      );
                    }

                    // Merge branding data into theme
                    if (parsedBranding.branding) {
                      finalTheme.brandTitle =
                        parsedBranding.branding.brandTitle;
                      finalTheme.tagline = parsedBranding.branding.tagline;
                      finalTheme.primaryColor =
                        parsedBranding.branding.primaryColor;
                      finalTheme.secondaryColor =
                        parsedBranding.branding.secondaryColor;
                      console.log(
                        "✅ Merged branding data into document theme"
                      );
                    }

                    // Clean up session storage after loading
                    sessionStorage.removeItem(
                      `document_${documentId}_branding`
                    );
                  }
                } catch (error) {
                  console.warn(
                    "⚠️ Error loading branding data from session storage:",
                    error
                  );
                }

                setDocumentTheme(finalTheme);

                // Set client info if available
                if (result.document.client) {
                  // For now, we don't have a way to get client ID from the service
                  // but we have the client name
                  setDocumentData((prev) => ({
                    ...prev,
                    client: result.document.client,
                  }));
                }

                // toast({
                //   title: "Document Loaded",
                //   description: "Document loaded successfully from database",
                // });
              } else {
                throw new Error("Document not found");
              }
            } catch (dbError) {
              console.error("Error loading document from database:", dbError);
              console.error("Document ID that failed:", documentId);
              console.error("Error details:", JSON.stringify(dbError, null, 2));

              // 🚨 ENHANCED: Try to load from session storage before showing error
              console.log("🔄 Database failed, attempting session storage fallback");
              let sessionLoadSuccessful = false;

              try {
                const sessionData = sessionStorage.getItem("scopingai_generated_document");
                if (sessionData) {
                  const parsed = JSON.parse(sessionData);
                  if (parsed.documentData && parsed.editableSections) {
                    console.log("✅ Loading document from session storage after database error");
                    setDocumentData(parsed.documentData);
                    setEditableSections(parsed.editableSections);
                    setDocumentTheme(parsed.documentTheme || {});
                    if (parsed.documentDbId) {
                      setDocumentDbId(parsed.documentDbId);
                    }

                    // Create pages from sections
                    const newPages = createPagesDirectly(parsed.editableSections);
                    setPages(newPages);

                    sessionLoadSuccessful = true;
                    console.log("✅ Successfully loaded document from session storage");
                  }
                }
              } catch (sessionError) {
                console.error("Error loading from session storage:", sessionError);
              }

              // Only show error if session storage loading also failed
              if (!sessionLoadSuccessful) {
                console.log("❌ Session storage fallback also failed, showing error content");

                // Create fallback content as last resort
                const fallbackSections = [
                  {
                    id: "section-1",
                    title: "Error Loading Document",
                    content:
                      "There was an error loading this document. You can edit this content and save it.",
                    blocks: convertContentToBlocks(
                      "There was an error loading this document. You can edit this content and save it."
                    ),
                    status: "completed" as const,
                    order: 1,
                  },
                ];

                setEditableSections(fallbackSections);
                const fallbackPages = createPagesDirectly(fallbackSections);
                setPages(fallbackPages);

                console.log("Set fallback sections:", fallbackSections);
                console.log("Set fallback pages:", fallbackPages);

                toast({
                  title: "Error Loading Document",
                  description: `Could not load document from database. Document ID: ${documentId}. Error: ${
                    dbError instanceof Error ? dbError.message : "Unknown error"
                  }`,
                  variant: "destructive",
                });

                // Create a basic document structure as fallback
                setDocumentData({
                  title: titleFromURL || `Document ${documentId}`,
                  client: "",
                  type: "Document",
                  sections: fallbackSections,
                });
              }
            }
          }
        } else {
          // For new documents or if no document ID, set a dummy section for testing
          console.log("No document ID, creating new document");

          const sampleContent =
            "This is a sample section. You can edit this content or regenerate it with AI.";
          const sampleBlocks = convertContentToBlocks(sampleContent);

          setEditableSections([
            {
              id: "section-1",
              title: "Introduction",
              content: sampleContent,
              blocks: sampleBlocks,
              status: "completed",
              order: 1,
            },
          ]);

          // Initialize the pages with the sample section
          const newPages = createPagesDirectly([
            {
              id: "section-1",
              title: "Introduction",
              content: sampleContent,
              blocks: sampleBlocks,
              status: "completed",
              order: 1,
            },
          ]);
          setPages(newPages);

          if (titleFromURL) {
            setDocumentData((prev) => ({ ...prev, title: titleFromURL }));
          }
        }
      } catch (error) {
        console.error("Error loading document:", error);
        toast({
          title: "Error Loading Document",
          description:
            "There was a problem loading the document. Please try again.",
          variant: "destructive",
        });

        // Ensure we have fallback content even on critical errors
        const fallbackSections = [
          {
            id: "section-1",
            title: "Error Loading Document",
            content:
              "There was an error loading this document. You can edit this content and save it.",
            blocks: convertContentToBlocks(
              "There was an error loading this document. You can edit this content and save it."
            ),
            status: "completed" as const,
            order: 1,
          },
        ];

        setEditableSections(fallbackSections);
        const fallbackPages = createPagesDirectly(fallbackSections);
        setPages(fallbackPages);
      } finally {
        // Always set loading to false, even if there's an error
        setIsLoading(false);

        // Final safety check - ensure pages is never empty
        setTimeout(() => {
          // Use a callback to get the current state
          setPages((currentPages) => {
            if (currentPages.length === 0) {
              console.warn(
                "Pages array is empty after loading, creating emergency fallback"
              );
              const emergencySection = {
                id: "emergency-section",
                title: "Document Content",
                content:
                  "Content is being loaded. Please refresh the page if this persists.",
                blocks: convertContentToBlocks(
                  "Content is being loaded. Please refresh the page if this persists."
                ),
                status: "completed" as const,
                order: 1,
              };
              setEditableSections([emergencySection]);
              return createPagesDirectly([emergencySection]);
            }
            return currentPages;
          });
        }, 100);
      }
    };

    loadDocumentData();
  }, [documentId, titleFromURL, toast]);

  // Sync editableSections with documentData and pages for preview updates
  useEffect(() => {
    if (editableSections.length > 0) {
      // Update documentData.sections to match editableSections
      setDocumentData((prev) => ({
        ...prev,
        sections: editableSections,
      }));

      // 🚨 EMERGENCY FIX: Update pages directly without splitIntoPages
      const sectionsPerPage = 3;
      const newPages = [];

      for (let i = 0; i < editableSections.length; i += sectionsPerPage) {
        const pageNumber = Math.floor(i / sectionsPerPage) + 1;
        const sectionsForPage = editableSections.slice(i, i + sectionsPerPage);

        newPages.push({
          id: `page-${pageNumber}`,
          content: sectionsForPage
        });
      }

      setPages(newPages);
      console.log('🚨 EMERGENCY: Updated pages directly, all sections preserved:', newPages.length, 'pages');
    }
  }, [editableSections]);

  // Section and Block management functions
  const handleUpdateSectionTitle = useCallback(
    (sectionId: string, newTitle: string) => {
      setEditableSections((prev) =>
        prev.map((section) =>
          section.id === sectionId ? { ...section, title: newTitle } : section
        )
      );
      setIsEdited(true);
    },
    []
  );

  const handleUpdateBlock = useCallback(
    (sectionId: string, blockId: string, updates: Partial<Block>) => {
      setEditableSections((prev) =>
        prev.map((section) => {
          if (section.id === sectionId) {
            const updatedBlocks = section.blocks.map((block) =>
              block.id === blockId ? { ...block, ...updates } : block
            );
            // Regenerate content from blocks
            const updatedContent = convertBlocksToContent(updatedBlocks);
            return {
              ...section,
              blocks: updatedBlocks,
              content: updatedContent,
            };
          }
          return section;
        })
      );
      setIsEdited(true);
    },
    []
  );

  // New function to update entire section content (for live generation)
  const handleUpdateSectionContent = useCallback(
    (sectionId: string, newContent: string) => {
      setEditableSections((prev) =>
        prev.map((section) => {
          if (section.id === sectionId) {
            // Treat all content as Markdown
            return {
              ...section,
              content: newContent,
              blocks: convertContentToBlocks(newContent),
            };
          }
          return section;
        })
      );
      setIsEdited(true);
    },
    []
  );

  const handleAddBlock = useCallback(
    (sectionId: string, type: Block["type"]) => {
      setEditableSections((prev) =>
        prev.map((section) => {
          if (section.id === sectionId) {
            const newBlock: Block = {
              id: `block-${Date.now()}-${Math.random()
                .toString(36)
                .substring(2, 9)}`,
              type,
              content: "",
              ...(type === "header" && { level: 2 }),
              ...(type === "list" && { items: [""] }),
            };

            const updatedBlocks = [...section.blocks, newBlock];
            // Regenerate content from blocks
            const updatedContent = convertBlocksToContent(updatedBlocks);

            return {
              ...section,
              blocks: updatedBlocks,
              content: updatedContent,
            };
          }
          return section;
        })
      );
      setIsEdited(true);
    },
    []
  );

  const handleDeleteBlock = useCallback(
    (sectionId: string, blockId: string) => {
      setEditableSections((prev) =>
        prev.map((section) => {
          if (section.id === sectionId) {
            const updatedBlocks = section.blocks.filter(
              (block) => block.id !== blockId
            );
            // Regenerate content from blocks
            const updatedContent = convertBlocksToContent(updatedBlocks);

            return {
              ...section,
              blocks: updatedBlocks,
              content: updatedContent,
            };
          }
          return section;
        })
      );
      setIsEdited(true);
    },
    []
  );

  const handleDeleteSection = useCallback(
    (sectionId: string) => {
      setEditableSections((prev) => {
        const updatedSections = prev.filter((section) => section.id !== sectionId);
        
        // If this was the selected section, clear the selection
        if (selectedSectionId === sectionId) {
          setSelectedSectionId(null);
        }
        
        return updatedSections;
      });
      setIsEdited(true);
    },
    [selectedSectionId]
  );

  // Function to handle AI regeneration
  const handleRegenerateWithAI = async (
    sectionId: string,
    enhancementType: string
  ) => {
    try {
      // Find the section to regenerate
      const sectionToRegenerate = editableSections.find(
        (section) => section.id === sectionId
      );

      if (!sectionToRegenerate) {
        toast({
          title: "Error",
          description: "Section not found",
          variant: "destructive",
        });
        return;
      }

      // Set loading state
      setRegeneratingSection(sectionId);

      const response = await regenerateSectionWithAI(
        sectionToRegenerate.content,
        sectionToRegenerate.title,
        enhancementType,
        documentData.title
      );

      if (response.success) {
        // Convert the regenerated content to blocks
        const regeneratedBlocks = convertContentToBlocks(
          response.regeneratedContent
        );

        // Process and fix any image blocks - ensure they render properly
        const processedBlocks = regeneratedBlocks.map((block) => {
          if (block.type === "image") {
            // Extract caption and image URL properly
            const caption = block.caption || "";
            // If it's already a data URL or valid URL, keep it
            if (
              block.content.startsWith("data:image") ||
              block.content.startsWith("http")
            ) {
              return block;
            }
            // For image markdown references, try to preserve the original image if possible
            else {
              // Look for existing image blocks to preserve
              const existingImageBlock = sectionToRegenerate.blocks.find(
                (b) => b.type === "image"
              );
              if (existingImageBlock && existingImageBlock.content) {
                return {
                  ...block,
                  content: existingImageBlock.content,
                  caption: block.caption || existingImageBlock.caption,
                };
              }
              return block;
            }
          }
          return block;
        });

        // Update the section with regenerated content
        setEditableSections((prev) =>
          prev.map((section) =>
            section.id === sectionId
              ? {
                  ...section,
                  content: response.regeneratedContent,
                  blocks: processedBlocks,
                  status: "completed",
                }
              : section
          )
        );

        // Also mark document as edited
        setIsEdited(true);

        // Show success toast
        toast({
          title: "Content regenerated",
          description: `The ${sectionToRegenerate.title} section has been enhanced.`,
        });
      } else {
        throw new Error("Failed to regenerate content");
      }
    } catch (error) {
      console.error("Error regenerating content:", error);
      toast({
        title: "Regeneration failed",
        description:
          "There was an error enhancing the content. Please try again.",
        variant: "destructive",
      });
    } finally {
      // Clear loading state
      setRegeneratingSection(null);
    }
  };

  // 🚨 NEW: Prevent duplicate saves with request tracking
  const [saveRequestId, setSaveRequestId] = useState<string | null>(null);
  const lastSaveTimeRef = useRef<number>(0);

  // Save document function
  const saveDocumentToSupabase = async () => {
    try {
      // 🚨 CRITICAL: Prevent duplicate saves
      if (isSaving) {
        console.warn("⚠️ Save already in progress, ignoring duplicate request");
        return;
      }

      // Prevent saves within 2 seconds of each other
      const now = Date.now();
      const timeSinceLastSave = now - lastSaveTimeRef.current;
      if (timeSinceLastSave < 2000) {
        console.warn(`⚠️ Save blocked - only ${timeSinceLastSave}ms since last save (minimum 2000ms)`);
        return;
      }
      lastSaveTimeRef.current = now;

      // Generate unique request ID to prevent duplicates
      const requestId = `save_${Date.now()}_${Math.random().toString(36).substring(2, 11)}`;
      setSaveRequestId(requestId);
      console.log(`🚨 SAVE REQUEST STARTED: ${requestId}`);

      // Check if title is provided
      if (!documentData.title.trim()) {
        toast({
          title: "Title Required",
          description: "Please enter a document title before saving",
          variant: "destructive",
        });
        return;
      }

      setIsSaving(true);

      // 🚨 FIXED: NO DEDUPLICATION - Save all sections as-is
      const uniqueSections = editableSections; // Use all sections without filtering

      console.log('🔍 Saving all sections without deduplication:', {
        sectionsCount: editableSections.length,
        sectionIds: editableSections.map(s => s.id),
        sectionTitles: editableSections.map(s => s.title)
      });

      // 🚨 FIXED: Don't include sections in documentData to prevent duplication
      const updatedDocumentData = {
        ...documentData,
        theme: { ...documentTheme },
        sections: [], // 🚨 FIXED: Empty array - sections come from uniqueSections parameter
      };

      // 🚨 FIXED: Store original documentDbId state before save to determine redirect behavior
      const isNewDocument = !documentDbId;

      const result = await saveDocument(
        updatedDocumentData,
        uniqueSections, // Pass deduplicated sections
        documentTheme,
        documentDbId,
        clientId,
        promptTemplateId,
        scopeTemplateId,
        filePath
      );

      // 🚨 ENHANCED: Store the file path and document ID for future updates
      if (result.documentId) {
        console.log(`✅ Setting documentDbId to: ${result.documentId} (was: ${documentDbId})`);
        setDocumentDbId(result.documentId);
      }
      if (result.filePath) {
        setFilePath(result.filePath);
      }

      // Reset edited state after successful save
      setIsEdited(false);

      // 🚨 ENHANCED: Save document state to sessionStorage after successful save
      saveDocumentStateToSession();

      // 🚨 NEW: Return result for auto-save tracking
      const saveOperation = isNewDocument ? 'created' : 'updated';
      console.log(`✅ Document ${saveOperation} successfully with ID: ${result.documentId}`);

      return {
        success: true,
        documentId: result.documentId,
        operation: saveOperation,
        isNewDocument
      };

      // Comprehensive cleanup after successful save
      try {
        console.log("🧹 Starting comprehensive cleanup after document save...");

        // Clear document-specific session storage
        if (documentId) {
          sessionStorage.removeItem(`document_${documentId}`);
          sessionStorage.removeItem(`document_${documentId}_branding`);
          console.log(`✅ Cleared session storage for document: ${documentId}`);
        }

        // Clear general scoping AI session storage
        const scopingAiKeys = [
          "scopingai_proposal_data",
          "scopingai_clientData",
          "scopingai_selectedClient",
          "scopingai_projectInfo",
          "selectedTemplateFromLibrary",
          "scopingai_generated_url",
          "autoTriggerRegeneration"
        ];

        scopingAiKeys.forEach(key => {
          if (sessionStorage.getItem(key)) {
            sessionStorage.removeItem(key);
            console.log(`✅ Cleared session storage key: ${key}`);
          }
        });

        // Clear local storage
        const localStorageKeys = [
          "generatedContent",
          "aiPrompts",
          "documentTheme",
          "scopingai_temp_data"
        ];

        localStorageKeys.forEach(key => {
          if (localStorage.getItem(key)) {
            localStorage.removeItem(key);
            console.log(`✅ Cleared localStorage key: ${key}`);
          }
        });

        console.log("✅ Comprehensive cleanup completed after document save");
      } catch (error) {
        console.error("❌ Error during cleanup after save:", error);
      }

      // 🚨 ENHANCED: Only show toast and redirect for manual saves, not auto-saves
      const isAutoSave = sessionStorage.getItem('scopingai_auto_save_in_progress') === 'true';

      if (!isAutoSave) {
        toast({
          title: isNewDocument ? "Document Saved" : "Document Updated",
          description: isNewDocument
            ? "Your document has been saved successfully."
            : "Your document has been updated successfully.",
        });

        // 🚨 ENHANCED: No automatic redirect for manual saves - let user stay on page
        if (isNewDocument) {
          console.log("✅ New document saved manually - staying on current page");
        } else {
          console.log("✅ Document updated manually - staying on current page");
        }
      } else {
        // Clear the auto-save flag
        sessionStorage.removeItem('scopingai_auto_save_in_progress');
        console.log("✅ Auto-save completed - no toast or redirect needed");
      }

      // 🚨 NEW: Return success result for auto-save tracking
      return {
        success: true,
        documentId: result.documentId,
        operation: isNewDocument ? 'created' : 'updated',
        isNewDocument
      };
    } catch (error) {
      console.error("Error saving document:", error);
      toast({
        title: "Save Failed",
        description: "There was an error saving your document",
        variant: "destructive",
      });

      // 🚨 NEW: Return error result for auto-save tracking
      return {
        success: false,
        error: error.message || 'Unknown error occurred',
        operation: 'failed'
      };
    } finally {
      setIsSaving(false);
      setSaveRequestId(null);
      console.log(`🚨 SAVE REQUEST COMPLETED: ${saveRequestId}`);
    }
  };

  // Apply theme preset
  const applyThemePreset = (
    presetName: "professional" | "modern" | "creative" | "formal"
  ) => {
    setDocumentTheme(themePresets[presetName]);

    // Show toast notification
    toast({
      title: "Theme Applied",
      description: `Applied the ${presetName} theme to your document.`,
    });
  };

  // Save document theme
  const saveDocumentTheme = useCallback(() => {
    // Show loading indicator
    setIsLoading(true);

    try {
      console.log("Saving document theme...");
      console.log("Logo present:", !!documentTheme.logo);
      console.log(
        "Logo length:",
        documentTheme.logo ? documentTheme.logo.length : 0
      );
      console.log("Show cover page:", documentTheme.showCoverPage !== false);

      // Update document data with theme only on explicit save
      // This creates a complete, fresh copy of the theme to avoid reference issues
      setDocumentData((prev) => ({
        ...prev,
        theme: JSON.parse(JSON.stringify(documentTheme)),
      }));

      // Mark as edited
      setIsEdited(true);

      // Save to local storage or session storage depending on documentId
      if (documentId) {
        const existingData = JSON.parse(
          sessionStorage.getItem(`document_${documentId}`) || "{}"
        );

        // Ensure we're storing the complete theme with all properties
        sessionStorage.setItem(
          `document_${documentId}`,
          JSON.stringify({
            ...existingData,
            theme: documentTheme,
            // Explicitly add properties to ensure they're saved
            hasLogo: !!documentTheme.logo,
            logoData: documentTheme.logo || null,
            showCoverPage: documentTheme.showCoverPage !== false,
          })
        );
      } else {
        const existingData = JSON.parse(
          localStorage.getItem("generatedContent") || "{}"
        );

        // Ensure we're storing the complete theme with all properties
        localStorage.setItem(
          "generatedContent",
          JSON.stringify({
            ...existingData,
            theme: documentTheme,
            // Explicitly add properties to ensure they're saved
            hasLogo: !!documentTheme.logo,
            logoData: documentTheme.logo || null,
            showCoverPage: documentTheme.showCoverPage !== false,
          })
        );
      }

      toast({
        title: "Theme Saved",
        description: "Document theme has been updated.",
      });
    } catch (e) {
      console.error("Error saving theme:", e);
      toast({
        title: "Save Failed",
        description: "There was an error saving the theme",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  }, [documentId, documentTheme, toast]);

  // Theme property update functions
  const updateThemeProperty = useCallback(
    (property: keyof DocumentTheme, value: string) => {
      setDocumentTheme((prev) => ({
        ...prev,
        [property]: value,
      }));
      setIsEdited(true);
    },
    []
  );

  const updateHeaderProperty = useCallback((property: string, value: any) => {
    setDocumentTheme((prev) => ({
      ...prev,
      header: {
        ...prev.header,
        [property]: value,
      },
    }));
    setIsEdited(true);
  }, []);

  const updateFooterProperty = useCallback((property: string, value: any) => {
    setDocumentTheme((prev) => ({
      ...prev,
      footer: {
        ...prev.footer,
        [property]: value,
      },
    }));
    setIsEdited(true);
  }, []);

  const handleLogoUpload = useCallback(() => {
    const input = document.createElement("input");
    input.type = "file";
    input.accept = "image/*";
    input.onchange = (e: Event) => {
      const target = e.target as HTMLInputElement;
      if (target.files && target.files.length > 0) {
        const file = target.files[0];
        const reader = new FileReader();
        reader.onload = (event) => {
          if (event.target && typeof event.target.result === "string") {
            setDocumentTheme((prev) => ({
              ...prev,
              logo: event.target!.result as string,
            }));
            setIsEdited(true);
            toast({
              title: "Logo uploaded",
              description: "Your logo has been added to the document theme.",
            });
          }
        };
        reader.readAsDataURL(file);
      }
    };
    input.click();
  }, [toast]);

  const value = {
    documentData,
    editableSections,
    documentTheme,
    isLoading,
    isSaving,
    isEdited,
    overview,
    documentDbId,
    clientId,
    promptTemplateId,
    scopeTemplateId,
    filePath,
    aiPromptsSummary,
    selectedSectionId,
    regeneratingSection,
    pages,
    currentPage,

    // Methods
    setDocumentData,
    setEditableSections,
    setDocumentTheme,
    handleUpdateSectionTitle,
    handleUpdateBlock,
    handleUpdateSectionContent,
    handleAddBlock,
    handleDeleteBlock,
    handleDeleteSection,
    handleRegenerateWithAI,
    setCurrentPage,
    setSelectedSectionId,
    saveDocumentToSupabase,
    applyThemePreset,
    saveDocumentTheme,
    updateThemeProperty,
    updateHeaderProperty,
    updateFooterProperty,
    handleLogoUpload,
    saveDocumentStateToSession,
  };

  return (
    <DocumentContext.Provider value={value}>
      {children}
    </DocumentContext.Provider>
  );
};
